import { Company, User, Transaction, InviteCode, Product, Table, CashierShift, CashMovement, DeliveryOrder, DeliveryOrderStatus, InvoiceProductMapping, InvoiceSupplier, Supplier, SupplierProductPrice, AdminTeamMessage, BillingConfig, BillingPlanConfig, BillingPlanKey, SubscriptionNoticeRecurrence, SubscriptionStatus, FinancialEntry } from '../types';

/**
 * Backend configuration.
 * By default, localhost uses the online API so it does not depend on a local database.
 * To test a local backend, set REACT_APP_API_URL=http://localhost:3001.
 */
import { getPendingRequest, submitConfirmed, PendingRequest } from './pendingRequests';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://flowtab-app-production.up.railway.app';
if (process.env.REACT_APP_LOCAL_DEMO === 'true' && API_BASE_URL !== 'http://127.0.0.1:3022') {
    throw new Error('Ambiente de testes bloqueado: API não isolada.');
}
const LOCAL_API_BASE_URL = process.env.REACT_APP_LOCAL_DEMO === 'true' ? API_BASE_URL : 'http://localhost:3001';
const AUTH_TOKEN_KEY = 'ft_auth_token';
const CSRF_TOKEN_KEY = 'ft_csrf_token';
const OWNER_BASE_TOKEN_KEY = 'ft_owner_base_token';
const OWNER_BASE_USER_KEY = 'ft_owner_base_user';

const shouldUseCookieCredentials = () => {
    if (typeof window === 'undefined') return true;
    return !['localhost', '127.0.0.1'].includes(window.location.hostname);
};

const getStoredAuthToken = () => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(AUTH_TOKEN_KEY) || window.sessionStorage.getItem(AUTH_TOKEN_KEY);
};

export const hasApiAuthToken = () => Boolean(getStoredAuthToken());

export const setApiAuthToken = (token: string | undefined, rememberMe: boolean) => {
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
    window.sessionStorage.removeItem(AUTH_TOKEN_KEY);
    if (token) {
        const targetStorage = rememberMe ? window.localStorage : window.sessionStorage;
        targetStorage.setItem(AUTH_TOKEN_KEY, token);
    }
};

export const setApiCsrfToken = (csrfToken?: string) => {
    window.sessionStorage.removeItem(CSRF_TOKEN_KEY);
    if (csrfToken) {
        window.sessionStorage.setItem(CSRF_TOKEN_KEY, csrfToken);
    }
};

const getStoredCsrfToken = () => {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem(CSRF_TOKEN_KEY);
};

export const clearApiAuthToken = () => {
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
    window.sessionStorage.removeItem(AUTH_TOKEN_KEY);
    window.sessionStorage.removeItem(CSRF_TOKEN_KEY);
};

export const preserveOwnerBaseSession = () => {
    const token = window.localStorage.getItem(AUTH_TOKEN_KEY);
    const user = window.localStorage.getItem('ft_user_session');
    if (token && user) {
        window.localStorage.setItem(OWNER_BASE_TOKEN_KEY, token);
        window.localStorage.setItem(OWNER_BASE_USER_KEY, user);
    }
};

export const restoreOwnerBaseSession = () => {
    if (getStoredAuthToken()) return null;
    const token = window.localStorage.getItem(OWNER_BASE_TOKEN_KEY);
    const user = window.localStorage.getItem(OWNER_BASE_USER_KEY);
    if (!token || !user) return null;
    window.localStorage.setItem(AUTH_TOKEN_KEY, token);
    window.localStorage.setItem('ft_user_session', user);
    try { return JSON.parse(user); } catch { return null; }
};

export const clearOwnerBaseSession = () => {
    window.localStorage.removeItem(OWNER_BASE_TOKEN_KEY);
    window.localStorage.removeItem(OWNER_BASE_USER_KEY);
};

const isUnsafeRequest = (method?: string) => {
    const normalizedMethod = (method || 'GET').toUpperCase();
    return !['GET', 'HEAD', 'OPTIONS'].includes(normalizedMethod);
};

const normalizeInviteCode = (value?: string) => String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
const normalizeEmailValue = (value?: string) => String(value || '').trim().toLowerCase();

const isCsrfExemptEndpoint = (endpoint: string) => {
    return endpoint === '/login'
        || endpoint === '/register'
        || endpoint === '/invitation-codes/validate'
        || endpoint === '/waiter-login'
        || endpoint === '/logout'
        || endpoint.startsWith('/password-reset/')
        || endpoint.startsWith('/public/');
};

const fetchCsrfToken = async () => {
    const token = getStoredAuthToken();
    const response = await fetch(`${API_BASE_URL}/csrf-token`, {
        credentials: shouldUseCookieCredentials() ? 'include' : 'omit',
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });
    if (!response.ok) return null;
    const data = await response.json().catch(() => null);
    if (data?.csrfToken) setApiCsrfToken(data.csrfToken);
    return data?.csrfToken || null;
};

const request = async (endpoint: string, options: RequestInit = {}) => {
    const token = getStoredAuthToken();
    let csrfToken = getStoredCsrfToken();
    if (isUnsafeRequest(options.method) && !isCsrfExemptEndpoint(endpoint) && !csrfToken) {
        csrfToken = await fetchCsrfToken();
    }
    try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...options,
            credentials: shouldUseCookieCredentials() ? 'include' : 'omit',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                ...(csrfToken && isUnsafeRequest(options.method) && !isCsrfExemptEndpoint(endpoint) ? { 'X-CSRF-Token': csrfToken } : {}),
                ...options.headers,
            },
        });
        
        if (!response.ok) {
            const err = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
            if (isUnsafeRequest(options.method) && (endpoint === '/tables' || endpoint.startsWith('/tables/') || endpoint.includes('delivery-orders'))) return { requestError: err.error || 'Envio recusado.', status: response.status };
            console.error(`API Error on ${endpoint}:`, err.message || err.error || 'Server returned non-OK status');
            if (endpoint.startsWith('/billing/mercadopago/') || endpoint.startsWith('/migration/')) {
                return {
                    error: err.error || err.message || 'Não foi possível concluir a operação.',
                    detail: err.detail,
                    reason: err.reason,
                    config: err.config,
                } as any;
            }
            if (endpoint === '/register') {
                return {
                    success: false,
                    message: err.message || err.error || 'Não foi possível criar a conta agora.'
                } as any;
            }
            if (endpoint === '/stock/import-invoice' && response.status === 409) {
                return {
                    error: err.error || err.message || 'Esta nota já foi importada neste mês.',
                    code: err.code,
                    monthKey: err.monthKey,
                } as any;
            }
            const isAuthAttempt = ['/login', '/waiter-login', '/manager-auth'].includes(endpoint);
            if (response.status === 401 && !isAuthAttempt && typeof window !== 'undefined') {
                clearApiAuthToken();
                window.localStorage.removeItem('ft_user_session');
                window.sessionStorage.removeItem('ft_user_session');
                window.localStorage.setItem('ft_relogin_notice', '1');
                window.dispatchEvent(new Event('ft-auth-expired'));
            }
            if (endpoint === '/stock/invoice-photo-ocr') {
                return { success: false, error: err.error || err.message || 'Não foi possível ler a foto da nota.' } as any;
            }
            return null; // Retorna nulo em vez de lançar erro
        }
        
        if (response.status === 204) { // No Content
            return { success: true };
        }

        return await response.json();
    } catch (e: any) {
        console.error(`[MySQL ERROR] ${endpoint}:`, e.message);
        return null; // Retorna nulo em vez de lançar erro
    }
};

const sendPacket = (packet: PendingRequest) => request(packet.endpoint, { method: 'POST', body: JSON.stringify(packet.body) });
const tableRequest = (endpoint: string, body: unknown) => submitConfirmed({ endpoint, body }, sendPacket);

export const api = {
  recoverPending: async (companyId: string) => {
    const pending = getPendingRequest(companyId);
    return pending ? submitConfirmed(pending, sendPacket, true) : null;
  },
  auth: {
    getTutorialStatus: async (tutorial: 'pos' | 'products' | 'stock' | 'reports' | 'settings'): Promise<{ completed: boolean } | null> => request(`/tutorials/${tutorial}`),
    completeTutorial: async (tutorial: 'pos' | 'products' | 'stock' | 'reports' | 'settings'): Promise<{ success: boolean } | null> => request(`/tutorials/${tutorial}/complete`, { method: 'POST' }),
    getAccessTutorialStatus: async (): Promise<{ seen: boolean } | null> => request('/access-tutorial'),
    completeAccessTutorial: async (): Promise<{ success: boolean } | null> => request('/access-tutorial/complete', { method: 'POST' }),
    login: async (email: string, password: string, rememberMe = false) => request('/login', {
        method: 'POST',
        body: JSON.stringify({ email, password, rememberMe })
    }),
    waiterLogin: async (companyEmail: string, waiterName: string, accessCode: string) => request('/waiter-login', {
        method: 'POST',
        body: JSON.stringify({ companyEmail, waiterName, accessCode })
    }),
    verifyManager: async (password: string) => request('/manager-auth', {
        method: 'POST',
        body: JSON.stringify({ password })
    }),
    requestPasswordReset: async (email: string) => request('/password-reset/request', {
        method: 'POST',
        body: JSON.stringify({ email })
    }),
    resetPassword: async (token: string, password: string) => request('/password-reset/confirm', {
        method: 'POST',
        body: JSON.stringify({ token, password })
    }),
    logout: async () => request('/logout', { method: 'POST' }),
    createWaiterAccessToken: async (companyId: string): Promise<{ token: string; expiresInMinutes: number; qrPayload: string } | null> => (
        request(`/waiter-access/${companyId}/token`, { method: 'POST' })
    ),
  },

  cashierShifts: {
    getStatus: async (companyId: string): Promise<CashierShift | null> => request(`/cashier-shifts/status/${companyId}`),
    open: async (companyId: string, openingFloat: number, openingDetails?: any): Promise<CashierShift | null> => {
        return request('/cashier-shifts/open', {
            method: 'POST',
            body: JSON.stringify({ companyId, openingFloat, openingDetails })
        });
    },
    addMovement: async (shiftId: string, movement: Omit<CashMovement, 'id' | 'timestamp'>): Promise<CashierShift | null> => {
        return request(`/cashier-shifts/movements/${shiftId}`, {
            method: 'POST',
            body: JSON.stringify(movement)
        });
    },
    close: async (shiftId: string, finalReport: any): Promise<CashierShift | null> => {
        return request(`/cashier-shifts/close/${shiftId}`, {
            method: 'POST',
            body: JSON.stringify({ finalReport })
        });
    },
    updateFinalReport: async (shiftId: string, finalReport: any): Promise<CashierShift | null> => {
        return request(`/cashier-shifts/${shiftId}/final-report`, {
            method: 'PATCH',
            body: JSON.stringify({ finalReport })
        });
    },
    getHistory: async (companyId: string): Promise<CashierShift[] | null> => request(`/cashier-shifts/history/${companyId}`),
  },

  maintenance: {
    runCompanyCleanup: async (companyId: string): Promise<any | null> => {
        return request(`/maintenance/company/${companyId}/run`, { method: 'POST' });
    },
  },

  billing: {
    getConfig: async (): Promise<BillingConfig | null> => request('/billing/config'),
    updateCommunicationSettings: async (subscriptionNoticeRecurrence: SubscriptionNoticeRecurrence): Promise<{ subscriptionNoticeRecurrence?: SubscriptionNoticeRecurrence } | null> => request('/billing/communication-settings', {
        method: 'PATCH',
        body: JSON.stringify({ subscriptionNoticeRecurrence })
    }),
    updateTextractSettings: async (globalMonthlyLimit: number): Promise<{ globalMonthlyLimit?: number } | null> => request('/billing/textract-settings', {
        method: 'PATCH',
        body: JSON.stringify({ globalMonthlyLimit })
    }),
    updatePlan: async (plan: BillingPlanKey, payload: Pick<BillingPlanConfig, 'price' | 'features'>): Promise<BillingPlanConfig | null> => {
        return request(`/billing/plans/${plan}`, {
            method: 'PATCH',
            body: JSON.stringify(payload)
        });
    },
    createMercadoPagoSubscription: async (companyId: string, plan: BillingPlanKey): Promise<{ initPoint?: string; subscriptionId?: string; status?: string; price?: number; error?: string } | null> => {
        return request('/billing/mercadopago/subscription', {
            method: 'POST',
            body: JSON.stringify({ companyId, plan })
        });
    },
    syncMercadoPagoSubscription: async (companyId: string): Promise<{ success?: boolean; company?: Company; status?: string; subscriptionStatus?: SubscriptionStatus; error?: string } | null> => {
        return request('/billing/mercadopago/sync', {
            method: 'POST',
            body: JSON.stringify({ companyId })
        });
    },
  },

  communications: {
    sendTestEmail: async (): Promise<any | null> => {
        return request('/communications/test-email', { method: 'POST' });
    },
    sendPreviewSuite: async (type?: string, companyName?: string): Promise<any | null> => {
        return request('/communications/preview-suite', {
            method: 'POST',
            body: JSON.stringify({ companyName: companyName || 'FlowTab Prévia', type })
        });
    },
    sendCustomEmail: async (payload: { companyIds: string[]; subject: string; title?: string; message: string; extraMessage?: string; ctaLabel?: string; ctaUrl?: string }): Promise<any | null> => {
        return request('/communications/custom-email', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    },
    previewCustomEmail: async (payload: { companyId?: string; subject: string; title?: string; message: string; extraMessage?: string; ctaLabel?: string; ctaUrl?: string }): Promise<any | null> => {
        return request('/communications/custom-email/preview', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    },
    sendWinbackEmail: async (payload: { companyIds: string[]; extraMessage?: string; subject?: string; message?: string; ctaLabel?: string; ctaUrl?: string }): Promise<any | null> => {
        return request('/communications/winback', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    },
    previewWinbackEmail: async (payload: { companyId?: string; extraMessage?: string; subject?: string; message?: string; ctaLabel?: string; ctaUrl?: string }): Promise<any | null> => {
        return request('/communications/winback/preview', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    },
  },

  tables: {
    getState: async (companyId: string): Promise<Table[] | null> => request(`/tables/company/${companyId}`),
    transfer: async (companyId: string, sourceId: number, destinationId: number, mergeWithDestination: boolean, sourceVersion: Table, destinationVersion: Table): Promise<{ source: Table; destination: Table; message: string }> => tableRequest('/tables/transfer', {
        companyId, sourceId, destinationId, mergeWithDestination, sourceVersion, destinationVersion, operationId: crypto.randomUUID()
    }),
    updateState: async (table: Table, companyId: string, baseState: Table, clientSubmissionId?: string) => tableRequest('/tables', {
        ...table, companyId, baseState, clientSubmissionId, operationId: crypto.randomUUID()
    }),
    close: async (table: Table, companyId: string, payments: import('../types').Payment[]): Promise<import('../types').TableCloseResult> => tableRequest('/tables/close', {
        companyId, tableId: table.id, expected: table, payments, operationId: crypto.randomUUID()
    }),
    cancel: async (table: Table, companyId: string): Promise<{ table: Table, restockedItemCount: number, lossItemCount: number } | null> => tableRequest('/tables/cancel', {
        companyId, tableId: table.id, expected: table, operationId: crypto.randomUUID()
    }),
    clearState: async (tableId: number, companyId: string) => {
        return request(`/tables/${companyId}/${tableId}`, { method: 'DELETE' });
    },
    clearAllByCompany: async (companyId: string) => {
        return request(`/tables/company/${companyId}`, { method: 'DELETE' });
    }
  },

  invites: {
    generate: async (adminId: string, adminName: string): Promise<InviteCode | null> => {
        return await request('/invitation-codes', {
            method: 'POST',
            body: JSON.stringify({ generatedById: adminId, generatedByName: adminName })
        });
    },
    validate: async (code: string): Promise<boolean> => {
        const res = await request(`/invitation-codes/validate`, { 
            method: 'POST', 
            body: JSON.stringify({ code: normalizeInviteCode(code) })
        });
        return res?.valid || false;
    },
    getActive: async () => request('/invitation-codes/active'),
    delete: async (id: string) => request(`/invitation-codes/${id}`, { method: 'DELETE' })
  },
  
  register: async (data: { companyName: string, ownerName: string, email: string, password: string, inviteCode: string }) => {
    return request('/register', {
        method: 'POST',
        body: JSON.stringify({
            ...data,
            companyName: data.companyName.trim(),
            ownerName: data.ownerName.trim(),
            email: normalizeEmailValue(data.email),
            inviteCode: normalizeInviteCode(data.inviteCode)
        })
    });
  },

  companies: {
    save: async (company: Company) => {
        return await request('/companies', { 
            method: 'POST', 
            body: JSON.stringify(company) 
        });
    },
    getAll: async () => request('/companies'),
    getById: async (id: string) => request(`/companies/${id}`),
    getByEmail: async (email: string) => request(`/companies/email/${email}`),
    // FIX: Added missing delete method to api.companies
    delete: async (id: string) => request(`/companies/${id}`, { method: 'DELETE' }),
    updateTableCount: async (companyId: string, count: number) => {
        return await request('/table-configs', {
            method: 'PATCH',
            body: JSON.stringify({ companyId, count })
        });
    },
    getTableCount: async (companyId: string) => {
        const res = await request(`/table-configs/${companyId}`);
        return res?.count || 12;
    },
    updateSettings: async (companyId: string, settings: Partial<Company>) => {
        return request(`/companies/${companyId}/settings`, {
            method: 'PATCH',
            body: JSON.stringify(settings)
        });
    },
  },

  users: {
    save: async (user: Partial<User> & Pick<User, 'name' | 'email'> & { currentPassword?: string }) => {
        return await request('/profiles', { 
            method: 'POST', 
            body: JSON.stringify(user) 
        });
    },
    delete: async (id: string) => {
        return await request(`/profiles/${id}`, { method: 'DELETE' });
    },
    getByEmail: async (email: string) => request(`/profiles/email/${email}`),
    getAdmins: async () => request('/profiles/admins'),
    getUsersByCompany: async (companyId: string) => request(`/profiles/company/${companyId}`)
  },

  adminTeam: {
    getMessages: async (): Promise<AdminTeamMessage[] | null> => request('/admin-team/messages'),
    sendMessage: async (payload: { message: string; priority?: 'NORMAL' | 'IMPORTANT' | 'URGENT'; targetAdminId?: string; targetCompanyId?: string }): Promise<AdminTeamMessage | null> => {
        return request('/admin-team/messages', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    },
    updateMessage: async (id: string, status: 'OPEN' | 'DONE'): Promise<AdminTeamMessage | null> => {
        return request(`/admin-team/messages/${id}`, {
            method: 'PATCH',
            body: JSON.stringify({ status })
        });
    }
  },

  managers: {
    create: async (name: string, password: string, companyId: string) => {
      return request('/managers', {
        method: 'POST',
        body: JSON.stringify({ name, password, companyId })
      });
    },
    getByCompany: async (companyId: string) => request(`/managers/company/${companyId}`),
    delete: async (id: string) => request(`/managers/${id}`, { method: 'DELETE' })
  },
  
  auditLogs: {
    create: async (action: string, managerName: string, companyId: string) => {
      return request('/audit-logs', {
        method: 'POST',
        body: JSON.stringify({ action, managerName, companyId })
      });
    },
    getByCompany: async (companyId: string, date?: string, month?: string) => {
      const query = date ? `date=${encodeURIComponent(date)}` : month ? `month=${encodeURIComponent(month)}` : '';
      return request(`/audit-logs/company/${companyId}${query ? `?${query}` : ''}`);
    },
  },

  products: {
    save: async (product: Product & { companyId: string }) => {
        return await request('/products', { 
            method: 'POST', 
            body: JSON.stringify(product) 
        });
    },
    delete: async (id: string) => {
        return await request(`/products/${id}`, { method: 'DELETE' });
    },
    getByCompany: async (companyId: string) => request(`/products/company/${companyId}`),
    updateStock: async (id: string, stockQuantity: number, lowStockThreshold?: number) => {
        return request(`/products/${id}/stock`, {
            method: 'PATCH',
            body: JSON.stringify({ stockQuantity, lowStockThreshold })
        });
    },
    addStockBatch: async (companyId: string, items: { productId: string; quantity: number }[]): Promise<{ updatedProducts: Product[] } | null> => {
        return request('/products/stock/bulk-add', {
            method: 'POST',
            body: JSON.stringify({ companyId, items })
        });
    },
    findCatalogImage: async (query: string): Promise<{ imageUrl: string; source: string } | null> => {
        const endpoint = `/catalog/product-image?query=${encodeURIComponent(query)}`;
        if (typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
            try {
                const response = await fetch(`${LOCAL_API_BASE_URL}${endpoint}`);
                if (response.ok) return response.json();
            } catch {
                // Fallback to the configured API below.
            }
        }
        return request(endpoint);
    }
  },

  suppliers: {
    getByCompany: async (companyId: string): Promise<Supplier[] | null> => request(`/suppliers/company/${companyId}`),
    save: async (supplier: Partial<Supplier> & { companyId: string; name: string }): Promise<Supplier | null> => request('/suppliers', {
      method: 'POST',
      body: JSON.stringify(supplier)
    }),
    delete: async (id: string) => request(`/suppliers/${id}`, { method: 'DELETE' }),
    getPricesByCompany: async (companyId: string): Promise<SupplierProductPrice[] | null> => request(`/supplier-product-prices/company/${companyId}`),
    savePrice: async (price: Partial<SupplierProductPrice> & { companyId: string; supplierId: string; productId: string; unitPrice: number }): Promise<SupplierProductPrice | null> => request('/supplier-product-prices', {
      method: 'POST',
      body: JSON.stringify(price)
    }),
    deletePrice: async (id: string) => request(`/supplier-product-prices/${id}`, { method: 'DELETE' })
  },

  invoiceMappings: {
    getByCompany: async (companyId: string): Promise<InvoiceProductMapping[] | null> => request(`/invoice-product-mappings/company/${companyId}`),
    save: async (mapping: Pick<InvoiceProductMapping, 'companyId' | 'supplierProductCode' | 'supplierProductName' | 'productId'>): Promise<InvoiceProductMapping | null> => {
        return request('/invoice-product-mappings', {
            method: 'POST',
            body: JSON.stringify(mapping)
        });
    },
    importStock: async (companyId: string, items: {
      supplierProductCode: string;
      supplierProductName: string;
      supplierBarcode?: string;
      supplierNcm?: string;
      supplierCfop?: string;
      supplierUnit?: string;
      unitPrice?: number;
      total?: number;
      quantity: number;
      productId: string;
    }[], supplier?: InvoiceSupplier, invoiceKey?: string, declaredTotal?: number): Promise<{ updatedProducts?: Product[], mappings?: InvoiceProductMapping[], supplier?: InvoiceSupplier, expense?: FinancialEntry, importRecord?: { id: string; monthKey: string; invoiceKey: string; source: string }, error?: string, code?: string, monthKey?: string } | null> => {
        return request('/stock/import-invoice', {
            method: 'POST',
          body: JSON.stringify({ companyId, items, supplier, invoiceKey, declaredTotal })
        });
    },
    getPhotoOcrConfig: async (companyId: string): Promise<{
      enabled: boolean;
      configured: boolean;
      plan: Company['plan'];
      monthKey: string;
      used: number;
      limit: number;
      remaining: number;
      globalLimit: number;
      globalUsed: number;
      globalRemaining: number;
      missingEnvNames: string[];
    } | null> => {
        return request(`/stock/invoice-photo-ocr/config/${companyId}`);
    },
    readPhotoOcr: async (companyId: string, payload: {
      content: string;
      mimeType: string;
    }): Promise<{
      success: boolean;
      error?: string;
      monthKey: string;
      used: number;
      limit: number;
      remaining: number;
      text: string;
      supplier?: InvoiceSupplier;
      declaredTotal?: number;
      provider?: 'TEXTRACT';
      items: {
        supplierProductCode: string;
        supplierProductName: string;
        supplierBarcode: string;
        supplierNcm: string;
        supplierCfop: string;
        unit: string;
        quantity: number;
        unitPrice: number;
        total: number;
        productId: string;
      }[];
    } | null> => {
        return request('/stock/invoice-photo-ocr', {
            method: 'POST',
            body: JSON.stringify({ companyId, ...payload })
        });
    }
  },

  categories: {
    save: async (name: string, companyId: string) => {
        return await request('/categories', {
            method: 'POST',
            body: JSON.stringify({ name, companyId })
        });
    },
    getByCompany: async (companyId: string) => request(`/categories/company/${companyId}`),
    delete: async (name: string, companyId: string) => {
        return await request('/categories', {
            method: 'DELETE',
            body: JSON.stringify({ name, companyId })
        });
    }
  },

  orders: {
    save: async (order: Transaction) => {
        return await request('/orders', { 
            method: 'POST', 
            body: JSON.stringify(order) 
        });
    },
    getByCompany: async (companyId: string, days = 31) => request(`/orders/company/${companyId}?days=${days}`),
  },

  financialEntries: {
    getByCompany: async (companyId: string, days = 31): Promise<FinancialEntry[] | null> => request(`/financial-entries/company/${companyId}?days=${days}`),
    create: async (entry: Omit<FinancialEntry, 'id' | 'createdAt'>): Promise<FinancialEntry | null> => request('/financial-entries', {
      method: 'POST',
      body: JSON.stringify(entry)
    }),
  },

  deliveryOrders: {
    save: async (order: DeliveryOrder): Promise<DeliveryOrder | null> => {
        return tableRequest('/delivery-orders', { ...order, operationId: crypto.randomUUID() });
    },
    getByCompany: async (companyId: string, days = 7): Promise<DeliveryOrder[] | null> => request(`/delivery-orders/company/${companyId}?days=${days}`),
    getById: async (id: string): Promise<DeliveryOrder | null> => request(`/delivery-orders/${id}`),
    updateStatus: async (id: string, status: DeliveryOrderStatus): Promise<DeliveryOrder | null> => {
        return await request(`/delivery-orders/${id}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status, operationId: crypto.randomUUID() })
        });
    },
    receivePayment: async (id: string): Promise<DeliveryOrder | null> => {
        return tableRequest(`/delivery-orders/${id}/receive-payment`, { operationId: crypto.randomUUID() });
    },
    savePublic: async (order: DeliveryOrder): Promise<DeliveryOrder | null> => {
        return tableRequest('/public/delivery-orders', { ...order, operationId: crypto.randomUUID() });
    },
    getPublicById: async (id: string, publicToken?: string): Promise<DeliveryOrder | null> => {
        const tokenQuery = publicToken ? `?token=${encodeURIComponent(publicToken)}` : '';
        return request(`/public/delivery-orders/${id}${tokenQuery}`);
    },
  },

  publicMenu: {
    getCompany: async (companyId: string): Promise<Company | null> => request(`/public/company/${companyId}`),
    getProducts: async (companyId: string): Promise<Product[] | null> => request(`/public/products/company/${companyId}`),
  }
};
